import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Link, useNavigate } from "react-router-dom";
import { RefreshCw, Search, Tv, Clock, Trophy, CalendarPlus, Share2, Star, BookmarkCheck, CalendarRange, X, LogOut, User as UserIcon, Gauge } from "lucide-react";
import GameRow from "@/components/GameRow";
import LoadingSkeleton from "@/components/LoadingSkeleton";
import EmptyState from "@/components/EmptyState";
import UpNextCard from "@/components/UpNextCard";
import SignInModal from "@/components/SignInModal";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { groupGamesByDate, formatDateHeader, formatGameTime, formatShortDate, TIMEZONES, gameMonthKey, formatMonthLabel, gameWeekKey, formatWeekLabel, gameMatchesQuickFilter, todayKey, tomorrowKey, upcomingWeekendKeys, gameDateKey } from "@/lib/dates";
import { platformInfo } from "@/lib/streamers";
import { gamesToIcs, downloadIcs, isExportable } from "@/lib/ics";
import { useFavorites } from "@/lib/favorites";
import { useSubscriptions } from "@/lib/subscriptions";
import { useAuth, formatApiError } from "@/lib/auth";
import { usePaywall, canUse } from "@/lib/access";
import { TrialBadge, TrialBanner } from "@/components/TrialNotices";
import StreamerDropdown from "@/components/StreamerDropdown";
import ChangePasswordButton from "@/components/ChangePasswordButton";
import {
  STORAGE_KEYS, QUICK_OPTIONS, RANGE_DAYS,
  readSavedSports, readSavedTz, readSavedStreamerFilter, readSavedSpecial,
  readSavedMonth, readSavedWeek, readSavedQuick, readSavedRangeStart,
  readUrlParams, buildShareUrl, copyText,
} from "@/lib/share";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ALL_SPORTS = [
  "NFL", "NBA", "WNBA", "MLB", "NHL", "MLS",
  "College Football",
  "Men's College Basketball", "Women's College Basketball",
  "Men's College Volleyball", "Women's College Volleyball",
  "Tennis", "Golf",
];

// Featured event — auto-hides the moment `endDate` passes so stale events
// (ended tournaments) stop cluttering the UI. Swap this config in for any
// future featured event (Super Bowl window, Olympics, March Madness, etc.).
const SPECIAL_EVENT = {
  name: "FIFA World Cup 2026",
  color: "#E10A17",
  // ISO date (end-of-day in UTC). After this timestamp the pill, state, and
  // share-url param are all suppressed automatically.
  endDate: "2026-07-19",
};

/** Returns the config iff the current date is <= endDate, else null. */
function activeSpecialEvent() {
  if (!SPECIAL_EVENT?.endDate) return null;
  const now = new Date();
  const end = new Date(`${SPECIAL_EVENT.endDate}T23:59:59Z`);
  if (isNaN(end.getTime()) || now > end) return null;
  return SPECIAL_EVENT;
}

const HERO_IMG = "https://images.unsplash.com/photo-1764863703740-2abbd49b14c5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzV8MHwxfHNlYXJjaHwxfHxzcG9ydHMlMjBzdGFkaXVtJTIwYXJjaGl0ZWN0dXJlfGVufDB8fHx8MTc3Nzk1MzQxM3ww&ixlib=rb-4.1.0&q=85";
const EMPTY_IMG = "https://images.unsplash.com/photo-1639161775388-db5b5d5cc9eb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDV8MHwxfHNlYXJjaHwxfHxlbXB0eSUyMHRlbm5pcyUyMGNvdXJ0JTIwbWluaW1hbHxlbnwwfHx8fDE3Nzc5NTM0MTZ8MA&ixlib=rb-4.1.0&q=85";

/** Top-right identity pill: shows "Sign in" when anon, email + sign-out when authed. */
function AuthChip() {
  const { user, status, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const authed = status === "in" && user;

  async function handleLogout() {
    await logout();
    // Hard reload to the landing page — guarantees the Sign-in button is
    // visible immediately with no stale auth state hanging around.
    window.location.assign("/");
  }

  if (authed) {
    return (
      <div className="flex items-center gap-2" data-testid="tracker-user-chip">
        <TrialBadge />
        <span className="hidden sm:inline-flex items-center gap-1.5 font-mono text-[10px] tracking-[0.2em] uppercase font-bold text-[#555]">
          <UserIcon size={11} strokeWidth={2.6} />
          {user.email}
          {user.role === "admin" && (
            <span className="ml-1 px-1.5 py-0.5 bg-[#111] text-[#f6f6f4] tracking-[0.2em]">ADMIN</span>
          )}
        </span>
        {user.role === "admin" && (
          <Link
            to="/admin"
            data-testid="tracker-admin-link"
            title="Admin dashboard"
            className="inline-flex items-center gap-1.5 font-mono text-[10px] tracking-[0.25em] uppercase font-bold border-2 border-[#111] bg-[#111] text-[#f6f6f4] px-3 py-1.5 hover:bg-[#333] transition-colors focus-ring active:translate-y-px"
          >
            <Gauge size={11} strokeWidth={2.6} />
            Admin
          </Link>
        )}
        {user.auth_provider !== "google" && <ChangePasswordButton />}
        <button
          type="button"
          onClick={handleLogout}
          data-testid="tracker-signout-btn"
          title="Sign out"
          className="inline-flex items-center gap-1.5 font-mono text-[10px] tracking-[0.25em] uppercase font-bold border-2 border-[#111] px-3 py-1.5 hover:bg-[#111] hover:text-[#f6f6f4] transition-colors focus-ring active:translate-y-px"
        >
          <LogOut size={11} strokeWidth={2.6} />
          Sign out
        </button>
      </div>
    );
  }

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid="tracker-signin-btn"
        className="inline-flex items-center gap-1.5 font-mono text-[10px] tracking-[0.25em] uppercase font-bold border-2 border-[#111] px-3 py-1.5 hover:bg-[#111] hover:text-[#f6f6f4] transition-colors focus-ring active:translate-y-px"
      >
        <UserIcon size={11} strokeWidth={2.6} />
        Sign in
      </button>
      <SignInModal open={open} onClose={() => setOpen(false)} defaultMode="signin" />
    </>
  );
}


const URL_PARAMS = readUrlParams(ALL_SPORTS);

export default function Tracker() {
  const [selected, setSelected] = useState(URL_PARAMS.sports ?? readSavedSports(ALL_SPORTS));
  const [specialOn, setSpecialOn] = useState(URL_PARAMS.special ?? readSavedSpecial());
  const [timezone, setTimezone] = useState(URL_PARAMS.tz ?? readSavedTz());
  const [streamerFilter, setStreamerFilter] = useState(URL_PARAMS.streamers ?? readSavedStreamerFilter());
  const [monthFilter, setMonthFilter] = useState(URL_PARAMS.month ?? readSavedMonth());
  const [weekFilter, setWeekFilter] = useState(URL_PARAMS.week ?? readSavedWeek());
  const [quickFilter, setQuickFilter] = useState(URL_PARAMS.quick ?? readSavedQuick());
  const [rangeStart, setRangeStart] = useState(URL_PARAMS.rangeStart ?? readSavedRangeStart());
  const [loading, setLoading] = useState(false);
  const [hasFetched, setHasFetched] = useState(false);
  const [games, setGames] = useState([]);
  const [fetchedAt, setFetchedAt] = useState(null);
  const [wasCached, setWasCached] = useState(false);
  const [favoritesOnly, setFavoritesOnly] = useState(() => {
    try { return localStorage.getItem("tracker.favoritesOnly.v1") === "1"; } catch { return false; }
  });
  const favs = useFavorites();
  const subs = useSubscriptions();
  const { user: authUser, status: authStatus, refreshUser } = useAuth();
  const { openSignIn, openPaywall } = usePaywall();
  const guideLocked = !canUse(authUser, "favorites");
  const activeSpecial = useMemo(() => activeSpecialEvent(), []);

  // Derived: inclusive end-key for the custom date range (rangeStart + 6 days).
  const rangeEndKey = useMemo(() => {
    if (!rangeStart) return "";
    try {
      const d = new Date(`${rangeStart}T00:00:00`);
      d.setDate(d.getDate() + (RANGE_DAYS - 1));
      return d.toISOString().slice(0, 10);
    } catch { return ""; }
  }, [rangeStart]);

  // Picking a date range clears the other mutually-exclusive filters so the
  // UI never shows a confusing "both" state.
  function applyRangeStart(iso) {
    setRangeStart(iso || "");
    if (iso) {
      setQuickFilter("");
      setMonthFilter("");
      setWeekFilter("");
    }
  }

  // Auto-clear the specialOn toggle if the event has ended. Prevents stale
  // event state from persisting across localStorage after the tournament.
  useEffect(() => {
    if (!activeSpecial && specialOn) setSpecialOn(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSpecial]);

  useEffect(() => {
    try { localStorage.setItem("tracker.favoritesOnly.v1", favoritesOnly ? "1" : "0"); } catch {}
  }, [favoritesOnly]);
  // Auto-disable "favorites only" if the user clears all their favorites so
  // they aren't left staring at an empty schedule.
  useEffect(() => {
    if (favoritesOnly && !favs.hasAny) setFavoritesOnly(false);
  }, [favoritesOnly, favs.hasAny]);

  useEffect(() => { localStorage.setItem(STORAGE_KEYS.sports, JSON.stringify(selected)); }, [selected]);
  useEffect(() => { localStorage.setItem(STORAGE_KEYS.tz, timezone); }, [timezone]);
  useEffect(() => { localStorage.setItem(STORAGE_KEYS.streamerFilter, JSON.stringify(streamerFilter)); }, [streamerFilter]);
  useEffect(() => { localStorage.setItem(STORAGE_KEYS.special, String(specialOn)); }, [specialOn]);
  useEffect(() => { localStorage.setItem(STORAGE_KEYS.month, monthFilter); }, [monthFilter]);
  useEffect(() => { localStorage.setItem(STORAGE_KEYS.week, weekFilter); }, [weekFilter]);
  useEffect(() => { localStorage.setItem(STORAGE_KEYS.quick, quickFilter); }, [quickFilter]);
  useEffect(() => { localStorage.setItem(STORAGE_KEYS.rangeStart, rangeStart); }, [rangeStart]);

  // Distinct streamer labels available in current results (from full platforms list)
  const availableStreamers = useMemo(() => {
    const seen = new Map();
    for (const g of games) {
      const list = (g.platforms && g.platforms.length > 0)
        ? g.platforms
        : (g.platform ? [g.platform] : []);
      for (const p of list) {
        const info = platformInfo(p);
        if (!seen.has(info.label)) seen.set(info.label, info.color);
      }
    }
    return Array.from(seen.entries()).map(([label, color]) => ({ label, color }));
  }, [games]);

  // Filter applied to games before grouping (matches if ANY platform is selected, AND month matches if set)
  const filteredGames = useMemo(() => {
    return games.filter((g) => {
      // streamer filter
      if (streamerFilter.length > 0) {
        const list = (g.platforms && g.platforms.length > 0)
          ? g.platforms
          : (g.platform ? [g.platform] : []);
        if (list.length === 0) return false;
        if (!list.some((p) => streamerFilter.includes(platformInfo(p).label))) return false;
      }
      // month filter
      if (monthFilter) {
        const mk = gameMonthKey(g, timezone);
        if (monthFilter === "TBD") {
          if (mk !== null) return false;
        } else if (mk !== monthFilter) {
          return false;
        }
      }
      // week filter
      if (weekFilter) {
        const wk = gameWeekKey(g, timezone);
        if (weekFilter === "TBD") {
          if (wk !== null) return false;
        } else if (wk !== weekFilter) {
          return false;
        }
      }
      // quick-jump filter (Tonight / Tomorrow / Weekend)
      if (quickFilter && !gameMatchesQuickFilter(g, quickFilter, timezone)) return false;
      // custom date range (user picks start → +7 days inclusive)
      if (rangeStart) {
        const key = gameDateKey(g, timezone);
        if (!key) return false;
        if (key < rangeStart || key > rangeEndKey) return false;
      }
      // favorites-only filter
      if (favoritesOnly && favs.hasAny && !favs.gameMatchesFavorites(g)) return false;
      return true;
    });
  }, [games, streamerFilter, monthFilter, weekFilter, quickFilter, rangeStart, rangeEndKey, timezone, favoritesOnly, favs]);

  // Months available in the full result set (ignoring current month filter so the dropdown stays stable)
  const availableMonths = useMemo(() => {
    const set = new Set();
    let hasTbd = false;
    for (const g of games) {
      const mk = gameMonthKey(g, timezone);
      if (mk) set.add(mk); else hasTbd = true;
    }
    const sorted = Array.from(set).sort();
    if (hasTbd) sorted.push("TBD");
    return sorted;
  }, [games, timezone]);

  // Weeks available — re-scoped by the *month* filter so the week dropdown stays
  // relevant when the user is browsing a single month. We do NOT re-scope by
  // streamer filter (week selection should survive that toggle).
  const availableWeeks = useMemo(() => {
    const set = new Set();
    let hasTbd = false;
    for (const g of games) {
      if (monthFilter) {
        const mk = gameMonthKey(g, timezone);
        if (monthFilter === "TBD") {
          if (mk !== null) continue;
        } else if (mk !== monthFilter) {
          continue;
        }
      }
      const wk = gameWeekKey(g, timezone);
      if (wk) set.add(wk); else hasTbd = true;
    }
    const sorted = Array.from(set).sort();
    if (hasTbd) sorted.push("TBD");
    return sorted;
  }, [games, monthFilter, timezone]);

  // If the current week filter is no longer in the available set (e.g. user
  // changed the month), clear it so we don't render an empty schedule with
  // no obvious cause.
  useEffect(() => {
    if (weekFilter && !availableWeeks.includes(weekFilter)) {
      setWeekFilter("");
    }
  }, [availableWeeks, weekFilter]);

  const groups = useMemo(() => groupGamesByDate(filteredGames, timezone), [filteredGames, timezone]);

  // Sort games WITHIN each date group so subscribed-streamer matchups
  // bubble to the top. Stable sort preserves original time order within
  // the "subscribed" and "not-subscribed" buckets.
  const sortedGroups = useMemo(() => {
    if (!subs.hasAny) return groups;
    const subsSet = new Set(subs.subscriptions);
    const gameHasSub = (g) => {
      const list = (g.platforms && g.platforms.length > 0)
        ? g.platforms
        : (g.platform ? [g.platform] : []);
      return list.some((p) => subsSet.has(platformInfo(p).label));
    };
    return groups.map(([key, list]) => [
      key,
      [...list].sort((a, b) => Number(gameHasSub(b)) - Number(gameHasSub(a))),
    ]);
  }, [groups, subs.hasAny, subs.subscriptions]);

  // Count games per quick-jump bucket (against all games, not the post-filter
  // set, so the pill counts reflect what the user *could* jump to).
  const quickCounts = useMemo(() => {
    const counts = { today: 0, tomorrow: 0, weekend: 0 };
    const tKey = todayKey();
    const tomKey = tomorrowKey();
    const weKeys = new Set(upcomingWeekendKeys());
    for (const g of games) {
      const raw = g?.date;
      const key = raw && /^\d{4}-\d{2}-\d{2}/.test(raw) ? raw.slice(0, 10) : null;
      if (!key) continue;
      if (key === tKey) counts.today += 1;
      if (key === tomKey) counts.tomorrow += 1;
      if (weKeys.has(key)) counts.weekend += 1;
    }
    return counts;
  }, [games]);

  function applyQuickFilter(id) {
    // Clicking the active pill clears it. Otherwise activate, and clear
    // month/week so the schedule jumps cleanly to that bucket.
    if (quickFilter === id) {
      setQuickFilter("");
      return;
    }
    setQuickFilter(id);
    setMonthFilter("");
    setWeekFilter("");
    applyRangeStart("");
  }

  function togglePill(sport) {
    setSelected((prev) => prev.includes(sport) ? prev.filter((s) => s !== sport) : [...prev, sport]);
  }
  function toggleStreamer(label) {
    setStreamerFilter((prev) => prev.includes(label) ? prev.filter((s) => s !== label) : [...prev, label]);
  }

  async function findGames(forceRefresh = false) {
    const sportsToFetch = [...selected];
    if (specialOn && activeSpecial) sportsToFetch.push(activeSpecial.name);
    if (sportsToFetch.length === 0) {
      toast.error("Pick at least one sport.");
      return;
    }
    if (authStatus !== "in") {
      toast("Create a free account to see the schedule.", { description: "3-day free trial, no card needed." });
      openSignIn("signup");
      return;
    }
    setLoading(true);
    try {
      const resp = await axios.post(
        `${API}/games/find`,
        { sports: sportsToFetch, force_refresh: !!forceRefresh },
        { timeout: 90000 }
      );
      setGames(resp.data?.games || []);
      setFetchedAt(resp.data?.fetched_at || new Date().toISOString());
      setWasCached(!!resp.data?.cached);
      setHasFetched(true);
      const count = resp.data?.games?.length || 0;
      if (count === 0) {
        toast("No games found.", { description: "Try fewer sports or refresh in a bit." });
      } else if (resp.data?.cached) {
        toast.success(`Loaded ${count} game${count === 1 ? "" : "s"} from cache.`);
      } else {
        toast.success(`Found ${count} game${count === 1 ? "" : "s"}.`);
      }
    } catch (e) {
      console.error(e);
      if (e?.response?.status === 402) {
        await refreshUser();
        return;
      }
      toast.error("Failed to fetch games.", {
        description: formatApiError(e) || "Network error.",
      });
    } finally {
      setLoading(false);
    }
  }

  // Auto-fetch once when the page is opened via a deep-link that has sports.
  // We intentionally only do this on the very first mount (no deps array
  // dependencies) so subsequent navigation never re-fetches behind the user.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (URL_PARAMS.autofetch && authStatus === "in" && !hasFetched) findGames(false); }, [authStatus]);

  function buildShareUrlCurrent() {
    return buildShareUrl({ selected, specialOn, quickFilter, monthFilter, weekFilter, rangeStart, streamerFilter, timezone });
  }

  async function handleShare() {
    const url = buildShareUrlCurrent();
    // Update the address bar so the user sees the deep-link they're sharing.
    try { window.history.replaceState({}, "", url); } catch {}
    const copied = await copyText(url);
    if (copied) {
      toast.success("Link copied — share it anywhere.");
    } else {
      toast("Copy failed — here's the link:", { description: url });
    }
  }

  return (
    <main className="min-h-screen w-full" data-testid="tracker-page">
      {/* Top marquee */}
      <div className="border-b-2 border-[#111111] bg-[#111111] text-[#f6f6f4] overflow-hidden">
        <div className="marquee py-2">
          {Array.from({ length: 2 }).map((_, k) => (
            <div key={k} className="flex items-center shrink-0">
              {["LIVE SPORTS", "STREAMING SCHEDULE", "WHERE TO WATCH", "POWERED BY AI", "SPORTS BROADSHEET", "EDITION 01"].map(
                (t, i) => (
                  <span key={`${k}-${i}`} className="px-8 font-mono text-xs tracking-[0.4em] uppercase">
                    {t} <span className="opacity-50 ml-8">/</span>
                  </span>
                )
              )}
            </div>
          ))}
        </div>
      </div>
      <TrialBanner />

      {/* Hero */}
      <header className="border-b-2 border-[#111111]">
        <div className="max-w-6xl mx-auto px-4 sm:px-8 pt-10 pb-8">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <span className="bullet" />
              <span className="font-mono text-xs tracking-[0.3em] uppercase text-[#555]">
                Vol. 01 · {new Date().toLocaleDateString("en-US", { month: "short", day: "2-digit", year: "numeric" }).toUpperCase()}
              </span>
            </div>
            <div className="flex items-center gap-3">
              <div className="hidden md:flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-[#555] font-bold">
                <Tv size={14} strokeWidth={2.4} />
                Streaming Edition
              </div>
              <AuthChip />
            </div>
          </div>

          <h1 className="tracker-logo mt-6 text-[15vw] md:text-[10vw] leading-[0.85]">
            guidedsport<span className="inline-block ml-1">.</span>
          </h1>

          <div className="mt-6 grid md:grid-cols-12 gap-4 items-end">
            <div className="md:col-span-8">
              <p className="font-display text-2xl md:text-3xl font-bold tracking-tight text-[#111] leading-tight">
                What sports are on tonight — and which streamer has them.
              </p>
              <p className="mt-3 text-[#555] max-w-2xl">
                Pick the sports you actually care about. We scan the open web, have an AI pull a clean schedule, and tell you which streamer carries it.
              </p>
            </div>
            <div className="md:col-span-4 hidden md:block">
              <div className="border-2 border-[#111] overflow-hidden">
                <img src={HERO_IMG} alt="Stadium" className="w-full h-40 object-cover grayscale" />
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Controls */}
      <section className="max-w-6xl mx-auto px-4 sm:px-8 py-10" data-testid="controls">
        <div className="flex items-center gap-3 mb-5">
          <span className="bullet" />
          <h2 className="font-mono text-xs tracking-[0.3em] uppercase text-[#555]">
            01 — Choose your sports
          </h2>
        </div>

        <div className="flex flex-wrap gap-2.5" data-testid="sports-pills">
          {ALL_SPORTS.map((s) => {
            const active = selected.includes(s);
            const id = s.toLowerCase().replace(/\s+/g, "-");
            return (
              <button
                key={s}
                className="pill focus-ring"
                data-active={active}
                onClick={() => togglePill(s)}
                data-testid={`sport-pill-${id}`}
                aria-pressed={active}
              >
                {s}
              </button>
            );
          })}
          <button className="pill focus-ring" onClick={() => setSelected([...ALL_SPORTS])} data-testid="select-all-pill">
            + ALL
          </button>
          <button className="pill focus-ring" onClick={() => setSelected([])} data-testid="clear-pill">
            CLEAR
          </button>
        </div>

        {/* Special / featured event pill — only renders while the event
            is still in-window (see activeSpecialEvent()). */}
        {activeSpecial && (
          <div className="mt-4 flex items-center gap-3" data-testid="special-row">
            <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#555] font-bold">
              Featured Event
            </span>
            <button
              type="button"
              onClick={() => setSpecialOn((v) => !v)}
              data-testid={`sport-pill-${activeSpecial.name.toLowerCase().replace(/\s+/g, '-')}`}
              data-active={specialOn}
              aria-pressed={specialOn}
              className="focus-ring inline-flex items-center gap-2 rounded-full px-5 py-2 text-sm font-bold uppercase tracking-[0.05em] transition-colors active:translate-y-px"
              style={{
                border: `2px solid ${activeSpecial.color}`,
                background: specialOn ? activeSpecial.color : "transparent",
                color: specialOn ? "#fff" : activeSpecial.color,
              }}
            >
              <Trophy size={14} strokeWidth={2.6} />
              {activeSpecial.name}
            </button>
          </div>
        )}

        <div className="mt-8 flex flex-wrap items-center gap-4">
          <button
            className="btn-block focus-ring"
            onClick={() => findGames(false)}
            disabled={loading || (selected.length === 0 && !specialOn)}
            data-testid="find-games-btn"
          >
            {loading ? (
              <span className="inline-flex items-center gap-3">
                <RefreshCw size={18} className="spin-slow" />
                Searching…
              </span>
            ) : (
              <span className="inline-flex items-center gap-3">
                <Search size={18} strokeWidth={2.6} />
                Find Games
              </span>
            )}
          </button>

          <button
            className="btn-ghost focus-ring"
            onClick={() => findGames(true)}
            disabled={loading || (selected.length === 0 && !specialOn)}
            data-testid="refresh-btn"
            title="Force re-fetch (bypass cache)"
          >
            <RefreshCw size={14} className={loading ? "spin-slow" : ""} />
            Refresh
          </button>

          {/* TZ selector */}
          <div className="inline-flex items-center gap-2" data-testid="timezone-selector-wrap">
            <Clock size={14} strokeWidth={2.4} className="text-[#555]" />
            <span className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#555] font-bold">TZ</span>
            <select
              className="border-2 border-[#111] bg-transparent px-2 py-1.5 font-mono text-xs uppercase tracking-[0.15em] font-bold focus-ring"
              value={timezone}
              onChange={(e) => setTimezone(e.target.value)}
              data-testid="timezone-select"
            >
              {TIMEZONES.map((tz) => (
                <option key={tz.id} value={tz.id}>{tz.label}</option>
              ))}
            </select>
          </div>

          {/* Month filter */}
          {availableMonths.length > 0 && (
            <div className="inline-flex items-center gap-2" data-testid="month-filter-wrap">
              <span className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#555] font-bold">Month</span>
              <select
                className="border-2 border-[#111] bg-transparent px-2 py-1.5 font-mono text-xs uppercase tracking-[0.15em] font-bold focus-ring"
                value={monthFilter}
                onChange={(e) => { setMonthFilter(e.target.value); setQuickFilter(""); applyRangeStart(""); }}
                data-testid="month-select"
              >
                <option value="">All months</option>
                {availableMonths.map((m) => (
                  <option key={m} value={m}>
                    {m === "TBD" ? "Date TBD" : formatMonthLabel(m)}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Week filter */}
          {availableWeeks.length > 1 && (
            <div className="inline-flex items-center gap-2" data-testid="week-filter-wrap">
              <span className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#555] font-bold">Week</span>
              <select
                className="border-2 border-[#111] bg-transparent px-2 py-1.5 font-mono text-xs uppercase tracking-[0.15em] font-bold focus-ring"
                value={weekFilter}
                onChange={(e) => { setWeekFilter(e.target.value); setQuickFilter(""); applyRangeStart(""); }}
                data-testid="week-select"
              >
                <option value="">All weeks</option>
                {availableWeeks.map((w) => (
                  <option key={w} value={w}>
                    {w === "TBD" ? "Date TBD" : formatWeekLabel(w)}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Date range picker — user picks any day, we show that day + 6 more.
              Always visible so users can set a window *before* fetching. */}
          {(
            <div className="inline-flex items-center gap-2" data-testid="range-filter-wrap">
              <span className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#555] font-bold">
                Pick dates
              </span>
              <Popover>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    className="inline-flex items-center gap-2 border-2 border-[#111] bg-transparent px-3 py-1.5 font-mono text-xs uppercase tracking-[0.15em] font-bold focus-ring hover:bg-[#111] hover:text-white transition-colors"
                    data-testid="range-trigger"
                    data-active={!!rangeStart}
                    style={rangeStart ? { background: "#111", color: "#fff" } : undefined}
                  >
                    <CalendarRange size={13} strokeWidth={2.6} />
                    {rangeStart
                      ? `${rangeStart} → +${RANGE_DAYS - 1}d`
                      : `${RANGE_DAYS}-day window`}
                  </button>
                </PopoverTrigger>
                <PopoverContent align="start" className="w-auto p-0 bg-[#fafaf9] border-2 border-[#111]">
                  <Calendar
                    mode="single"
                    selected={rangeStart ? new Date(`${rangeStart}T00:00:00`) : undefined}
                    onSelect={(d) => {
                      if (!d) { applyRangeStart(""); return; }
                      const iso = d.toISOString().slice(0, 10);
                      applyRangeStart(iso);
                    }}
                    initialFocus
                  />
                  <div className="p-2 border-t-2 border-[#111] font-mono text-[9px] tracking-[0.2em] uppercase text-[#555] font-bold flex items-center justify-between gap-2">
                    <span>
                      {rangeStart
                        ? `${rangeStart} → ${rangeEndKey}`
                        : `Pick a day to view ${RANGE_DAYS} days of games`}
                    </span>
                    {rangeStart && (
                      <button
                        type="button"
                        onClick={() => applyRangeStart("")}
                        className="inline-flex items-center gap-1 link-u"
                        data-testid="range-clear"
                      >
                        <X size={10} strokeWidth={2.8} />
                        Clear
                      </button>
                    )}
                  </div>
                </PopoverContent>
              </Popover>
            </div>
          )}


          {fetchedAt && (
            <div className="text-xs uppercase tracking-[0.25em] text-[#555] font-bold font-mono ml-1" data-testid="fetched-at">
              {wasCached ? "Cached" : "Updated"} {new Date(fetchedAt).toLocaleString()}
            </div>
          )}
        </div>
      </section>

      {/* Results */}
      <section className="max-w-6xl mx-auto px-4 sm:px-8 pb-24" data-testid="results">
        <div className="flex items-center justify-between gap-3 mb-6 flex-wrap">
          <div className="flex items-center gap-3">
            <span className="bullet" />
            <h2 className="font-mono text-xs tracking-[0.3em] uppercase text-[#555]">
              02 — The schedule
            </h2>
          </div>
          {hasFetched && !loading && (
            <div className="flex items-center gap-2 flex-wrap">
              <button
                type="button"
                className="btn-ghost focus-ring"
                onClick={handleShare}
                data-testid="share-schedule-btn"
                title="Copy a shareable link with your current sports & filters"
              >
                <Share2 size={14} strokeWidth={2.4} />
                Share schedule
              </button>
              {filteredGames.some(isExportable) && (
                <button
                  type="button"
                  className="btn-ghost focus-ring"
                  onClick={() => {
                    const exportable = filteredGames.filter(isExportable);
                    const ics = gamesToIcs(exportable);
                    if (!ics) {
                      toast("Nothing to export.", { description: "No games have a confirmed start time." });
                      return;
                    }
                    const stamp = new Date().toISOString().slice(0, 10);
                    downloadIcs(`guidedsport-${stamp}`, ics);
                    toast.success(`Exported ${exportable.length} game${exportable.length === 1 ? "" : "s"} to .ics`);
                  }}
                  data-testid="export-all-calendar-btn"
                  title="Download an .ics file with every game shown"
                >
                  <CalendarPlus size={14} strokeWidth={2.4} />
                  Add all to calendar
                </button>
              )}
            </div>
          )}
        </div>

        {/* Favorites filter — appears the moment the user stars their first team */}
        {hasFetched && !loading && favs.hasAny && (
          <div className="mb-6 flex items-center gap-3 flex-wrap" data-testid="favorites-filter-row">
            <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#555] font-bold">
              YOUR TEAMS
            </div>
            <button
              type="button"
              onClick={() => setFavoritesOnly((v) => !v)}
              aria-pressed={favoritesOnly}
              data-testid="favorites-only-toggle"
              className="pill focus-ring inline-flex items-center gap-2"
              data-active={favoritesOnly}
              title={favoritesOnly ? "Showing favorite teams only" : "Show only your favorite teams"}
            >
              <Star
                size={12}
                strokeWidth={2.6}
                fill={favoritesOnly ? "#067647" : "transparent"}
                style={{ color: favoritesOnly ? "#067647" : "#111" }}
              />
              Favorites only
              <span
                className="font-mono text-[10px] px-1.5 py-px"
                style={{
                  background: favoritesOnly ? "#fff" : "#111",
                  color: favoritesOnly ? "#111" : "#fff",
                  borderRadius: "999px",
                  lineHeight: 1,
                }}
              >
                {favs.count}
              </span>
            </button>
            <button
              type="button"
              onClick={() => favs.clear()}
              className="font-mono text-[10px] tracking-[0.25em] uppercase font-bold link-u text-[#555]"
              data-testid="favorites-clear-all"
              title="Unfollow every team"
            >
              Clear all
            </button>
          </div>
        )}

        {/* Quick-jump pills (Tonight / Tomorrow / This weekend) */}
        {hasFetched && !loading && (quickCounts.today + quickCounts.tomorrow + quickCounts.weekend) > 0 && (
          <div className="mb-6" data-testid="quick-jump-row">
            <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#555] font-bold mb-3">
              QUICK JUMP
            </div>
            <div className="flex flex-wrap gap-2">
              {QUICK_OPTIONS.map((opt) => {
                const n = quickCounts[opt.id] || 0;
                if (n === 0) return null;
                const active = quickFilter === opt.id;
                return (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => applyQuickFilter(opt.id)}
                    data-active={active}
                    data-testid={`quick-jump-${opt.id}`}
                    className="pill focus-ring inline-flex items-center gap-2"
                  >
                    {opt.label}
                    <span
                      className="font-mono text-[10px] px-1.5 py-px"
                      style={{
                        background: active ? "#fff" : "#111",
                        color: active ? "#111" : "#fff",
                        borderRadius: "999px",
                        lineHeight: 1,
                      }}
                    >
                      {n}
                    </span>
                  </button>
                );
              })}
              {quickFilter && (
                <button
                  type="button"
                  onClick={() => setQuickFilter("")}
                  className="font-mono text-[10px] tracking-[0.25em] uppercase font-bold link-u self-center ml-1"
                  data-testid="quick-jump-clear"
                >
                  Clear
                </button>
              )}
            </div>
          </div>
        )}

        {/* Streamer dropdowns: My streamers (pins games to top) + Filter by streamer */}
        {hasFetched && availableStreamers.length > 0 && !loading && (
          <div className="mb-8 flex flex-wrap items-center gap-3" data-testid="streamer-dropdowns">
            <div data-testid="my-streamers-row">
              <StreamerDropdown
                label="My streamers"
                icon={<BookmarkCheck size={12} strokeWidth={2.6} />}
                streamers={availableStreamers}
                isActive={(l) => subs.has(l)}
                onToggle={(l) => (guideLocked ? openPaywall("streamers") : subs.toggle(l))}
                onClear={() => subs.clear()}
                clearLabel="Clear"
                testId="my-streamers"
                chipPrefix="my-streamer-chip"
                slugify={(l) => l.replace(/\s+/g, "-").toLowerCase()}
              />
            </div>
            <div data-testid="streamer-filter">
              <StreamerDropdown
                label="Filter by streamer"
                streamers={availableStreamers}
                isActive={(l) => streamerFilter.includes(l)}
                onToggle={toggleStreamer}
                onClear={() => setStreamerFilter([])}
                clearLabel="Show all"
                testId="streamer-filter"
                chipPrefix="streamer-chip"
                slugify={(l) => l.toLowerCase().replace(/[^a-z0-9]+/g, "-")}
              />
            </div>
            {subs.hasAny && (
              <p className="font-mono text-[9px] tracking-[0.15em] uppercase text-[#777] font-bold">
                Games on your services are pinned to the top of each day.
              </p>
            )}
          </div>
        )}

        {loading && <LoadingSkeleton />}

        {!loading && !hasFetched && (
          <EmptyState
            title="Press Find Games"
            subtitle="Choose your sports and we’ll bring you the live streaming schedule"
            image={EMPTY_IMG}
          />
        )}

        {!loading && hasFetched && filteredGames.length === 0 && (
          <EmptyState
            title={
              favoritesOnly
                ? "No upcoming games for your favorite teams"
                : streamerFilter.length > 0
                  ? "No matches for selected streamers"
                  : "No matches found"
            }
            subtitle={
              favoritesOnly
                ? "Turn off 'Favorites only' or star more teams to see more games"
                : streamerFilter.length > 0
                  ? "Clear the streamer filter to see all games"
                  : "Try fewer sports, or hit refresh in a moment"
            }
            image={EMPTY_IMG}
          />
        )}

        {!loading && filteredGames.length > 0 && !guideLocked && (
          <UpNextCard games={filteredGames} timezone={timezone} />
        )}

        {!loading && sortedGroups.length > 0 && (
          <div className="space-y-12" data-testid="games-list">
            {sortedGroups.map(([dateKey, list]) => (
              <div key={dateKey}>
                <div className="date-header flex items-baseline justify-between gap-4">
                  <span data-testid={`date-header-${dateKey}`}>{formatDateHeader(dateKey)}</span>
                  <span className="font-mono text-xs tracking-[0.25em] text-[#555] font-bold">
                    {list.length} GAME{list.length === 1 ? "" : "S"}
                  </span>
                </div>
                <div className="mt-2">
                  {list.map((g, i) => (
                    <GameRow
                      key={g.id || `${dateKey}-${i}`}
                      game={g}
                      index={i}
                      displayTime={formatGameTime(g, timezone)}
                      displayDate={formatShortDate(dateKey)}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Footer */}
      <footer className="border-t-2 border-[#111111]">
        <div className="max-w-6xl mx-auto px-4 sm:px-8 py-8 flex flex-wrap items-center justify-between gap-4 text-xs uppercase tracking-[0.25em] text-[#555] font-bold">
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
            <span>© guidedsport — A Sports Broadsheet</span>
            <span className="opacity-40">·</span>
            <span className="normal-case tracking-[0.05em] text-[#888]" data-testid="affiliate-disclosure">
              Streamer links may earn us a commission. Pricing is unaffected.
            </span>
          </div>
          <div className="flex items-center gap-4">
            <span>Live web search</span>
            <span className="opacity-40">·</span>
            <span>AI-curated schedule</span>
          </div>
        </div>
      </footer>
    </main>
  );
}
